import lodash from 'lodash'

import {
  dateFormat,
  blockElementsStartTag,
  blockElementsEndTag,
  lastBrInSpan,
  multipleBrs,
} from '@/utils/regex'
import { formatConditionsOfContracts } from '@/modules/tenders/utils'
import { getTenderServiceOthersTitle } from '@/utils/tenderUtils'
import dateUtil from '@/utils/dateUtil'
import DiffMatchPatch from 'diff-match-patch'
import i18n from '@/utils/i18n'
import store from '@/store'

function strip (html = '') {
  const tmp = document.createElement('DIV')
  tmp.innerHTML = html
  tmp.setAttribute('style', 'width:100%; overflow-x:auto;')

  // Remove dangerous and redundant content
  // *Redundant repeated element are accidentally copied by user in the editor
  const dangerousAndRedundantSelector = [
    'script',
    'style',
    '[style*="display: none"]',
    '[data-mce-style*="display: none"]',
    '[style*="display:none"]',
    '[data-mce-style*="display:none"]',
  ].join(', ')

  Array.from(tmp.querySelectorAll(dangerousAndRedundantSelector))
    .forEach(element => element.remove())

  // Replace block elements with <span> + <br/>
  tmp.innerHTML = tmp.innerHTML
    .replace(blockElementsStartTag, '<span>')
    .replace(blockElementsEndTag, '</span><br>')
    .replace(multipleBrs, '<br>')
    .replace(lastBrInSpan, '</span>')

  return tmp.outerHTML
}

function getDiffMatchPatch (oldStrippedValue, newStrippedValue) {
  const dmp = new DiffMatchPatch()
  dmp.Diff_Timeout = 1
  dmp.Diff_EditCost = 5

  const diff = dmp.diff_main(oldStrippedValue, newStrippedValue)
  dmp.diff_cleanupSemantic(diff)
  for (let index = 0; index < diff.length; index++) {
    if (diff[index][1].endsWith('<') && index < diff.length) {
      diff[index][1] = diff[index][1].slice(0, -1)
      diff[index + 1][1] = '<' + diff[index + 1][1]
    }
  }
  const html = dmp.diff_prettyHtml(diff)
  return html.replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&para;<br>/g, '')
}

function arrayToDict (key, array = []) {
  return array.reduce((result, rec) => {
    result[rec[key]] = rec
    return result
  }, {})
}

export const tenderFields = {
  general: {
    fields: {
      closingDateExtensionRequested: {
        diff: (oldTender, newTender) => {
          if (newTender.closingDateExtended) {
            return [{ action: 'E', oldValue: 'No', value: 'Yes', title: 'Closing Date Extension Requested' }]
          }
          return null
        },
      },
      projectDescription: {
        diff: (oldTender, newTender) => {
          if (newTender.projectDescription !== oldTender.projectDescription) {
            return [{ action: 'E', oldValue: oldTender.projectDescription, value: newTender.projectDescription, title: 'Project Description' }]
          }
          return null
        },
      },
      projectLocation: {
        diff: (oldTender, newTender) => {
          if (newTender.projectLocation !== oldTender.projectLocation) {
            return [{ action: 'E', oldValue: oldTender.projectLocation, value: newTender.projectLocation, title: 'Project Location' }]
          }
          return null
        },
      },
      projectStart: {
        diff: (oldTender, newTender) => {
          if (newTender.projectStart !== oldTender.projectStart) {
            return [{ action: 'E', oldValue: oldTender.projectStart, value: newTender.projectStart, title: 'Tentative Project Start Date' }]
          }
          return null
        },
      },
      estimatedProjectDuration: {
        diff: (oldTender, newTender) => {
          if (newTender.estimatedProjectDuration !== oldTender.estimatedProjectDuration) {
            return [{ action: 'E', oldValue: oldTender.estimatedProjectDuration, value: newTender.estimatedProjectDuration, title: 'Estimated Project Duration' }]
          }
          return null
        },
      },
      closingDate: {
        diff: (oldTender, newTender) => {
          const parseValueFromTender = (tender) => ((dateUtil.convertDateToArray(tender.closingDate || '', dateFormat, '-') || []).join(' '))
          if (newTender.closingDate !== oldTender.closingDate) {
            return [{ action: 'E', oldValue: parseValueFromTender(oldTender), value: parseValueFromTender(newTender), title: 'Closing Date' }]
          }
          return null
        },
      },
      workingHours: {
        diff: (oldTender, newTender) => {
          const parseValueFromTender = (tender) => ((tender.workingHours || []).map(rec => Object.values(rec).join(' - ')).join('\n'))
          if (!lodash.isEqual(newTender.workingHours, oldTender.workingHours)) {
            if (!newTender.workingHours || newTender.workingHours.length <= 0) {
              return [{ action: 'D', value: parseValueFromTender(oldTender), title: 'Working Hours' }]
            } else if (!oldTender.workingHours) {
              return [{ action: 'N', value: parseValueFromTender(newTender), title: 'Working Hours' }]
            }
            return [{ action: 'E', oldValue: parseValueFromTender(oldTender), value: parseValueFromTender(newTender), title: 'Working Hours' }]
          }
          return null
        },
      },
      noisyWorkingHours: {
        diff: (oldTender, newTender) => {
          const parseValueFromTender = (tender) => ((tender.noisyWorkingHours || []).map(rec => Object.values(rec).join(' - ')).join('\n'))
          if (!lodash.isEqual(newTender.noisyWorkingHours, oldTender.noisyWorkingHours)) {
            if (!newTender.noisyWorkingHours || newTender.noisyWorkingHours.length <= 0) {
              return [{ action: 'D', value: parseValueFromTender(oldTender), title: 'Noisy Working Hours' }]
            } else if (!oldTender.noisyWorkingHours || oldTender.noisyWorkingHours.length <= 0) {
              return [{ action: 'N', value: parseValueFromTender(newTender), title: 'Noisy Working Hours' }]
            }
            return [{ action: 'E', oldValue: parseValueFromTender(oldTender), value: parseValueFromTender(newTender), title: 'Noisy Working Hours' }]
          }
          return null
        },
      },
      specialWorkingHours: {
        diff: (oldTender, newTender) => {
          if (newTender.specialWorkingHours !== oldTender.specialWorkingHours) {
            if (!oldTender.specialWorkingHours) {
              return [{ action: 'N', oldValue: oldTender.specialWorkingHours, value: newTender.specialWorkingHours, title: i18n.t('tender.general.specialWorkingHours') }]
            } else if (oldTender.specialWorkingHours && !newTender.specialWorkingHours) {
              return [{ action: 'D', oldValue: oldTender.specialWorkingHours, value: newTender.specialWorkingHours, title: i18n.t('tender.general.specialWorkingHours') }]
            }
            return [{ action: 'E', oldValue: oldTender.specialWorkingHours, value: newTender.specialWorkingHours, title: i18n.t('tender.general.specialWorkingHours') }]
          }
          return null
        },
      },
      paymentTerms: {
        diff: (oldTender, newTender) => {
          if (newTender.paymentTerms !== oldTender.paymentTerms) {
            if (!oldTender.paymentTerms) {
              return [{ action: 'N', oldValue: oldTender.paymentTerms, value: newTender.paymentTerms, title: 'Payment Terms' }]
            } else if (oldTender.paymentTerms && !newTender.paymentTerms) {
              return [{ action: 'D', oldValue: oldTender.paymentTerms, value: newTender.paymentTerms, title: 'Payment Terms' }]
            }
            return [{ action: 'E', oldValue: oldTender.paymentTerms, value: newTender.paymentTerms, title: 'Payment Terms' }]
          }
          return null
        },
      },
      optionalItems: {
        diff: (oldTender, newTender) => {
          const parseValueFromOptionalItem = rec => (`${rec.name}: ${rec.value} ${rec.text}`)

          const oldOptionalItems = arrayToDict('name', oldTender.optionalItems.filter(rec => rec.selected))
          const newOptionalItems = arrayToDict('name', newTender.optionalItems.filter(rec => rec.selected))
          let result = []
          result = result.concat(newTender.optionalItems.reduce((tmpResult, optionalItem) => {
            if ((!oldOptionalItems[optionalItem.name] ||
                !oldOptionalItems[optionalItem.name].selected) && optionalItem.selected) {
              tmpResult.push({ action: 'N', value: parseValueFromOptionalItem(optionalItem), title: `Additional Items: ${optionalItem.name}` })
            } else if (oldOptionalItems[optionalItem.name] && oldOptionalItems[optionalItem.name].selected && optionalItem.selected &&
                (String(oldOptionalItems[optionalItem.name].value) !== String(optionalItem.value) ||
                  String(oldOptionalItems[optionalItem.name].text) !== String(optionalItem.text))) {
              tmpResult.push({ action: 'E',
                oldValue: parseValueFromOptionalItem(oldOptionalItems[optionalItem.name]),
                value: parseValueFromOptionalItem(optionalItem),
                title: `Additional Items: ${optionalItem.name}` })
            }
            return tmpResult
          }, []))
          result = result.concat(oldTender.optionalItems.reduce((tmpResult, optionalItem) => {
            if ((!newOptionalItems[optionalItem.name] ||
              !newOptionalItems[optionalItem.name].selected) && optionalItem.selected) {
              tmpResult.push({ action: 'D', value: parseValueFromOptionalItem(optionalItem), title: `Additional Items: ${optionalItem.name}` })
            }
            return tmpResult
          }, []))
          return result
        },
      },
      validityPeriod: {
        diff: (oldTender, newTender) => {
          const parseValueFromTender = (tender) => `${tender.validityPeriod} ${i18n.t('tender.general.validityPeriodLabel')}`
          if (newTender.validityPeriod !== oldTender.validityPeriod) {
            return [{ action: 'E', oldValue: parseValueFromTender(oldTender), value: parseValueFromTender(newTender), title: 'Validity Period' }]
          }
          return null
        },
      },
      onsiteVisitRequired: {
        diff: (oldTender, newTender) => {
          const parseValueFromTender = (tender) => tender.onsiteVisitRequired ? 'YES' : 'NO'
          if (newTender.onsiteVisitRequired !== oldTender.onsiteVisitRequired) {
            return [{ action: 'E', oldValue: parseValueFromTender(oldTender), value: parseValueFromTender(newTender), title: 'On-site Visit Required' }]
          }
          return null
        },
      },
      conditionsOfContracts: {
        diff: (oldTender, newTender) => {
          const cocI18nPath = store.getters['tenders/cocI18nPath']
          const isOldTerm = store.getters['tenders/isOldTerm']
          const newCocType = newTender.conditionsOfContracts
          const oldCocType = oldTender.conditionsOfContracts
          let newValue = newCocType && i18n.t(cocI18nPath).length > 0 ? i18n.t(cocI18nPath)[0][newCocType.toLowerCase()] : ''
          let oldValue = oldCocType && i18n.t(cocI18nPath).length > 0 ? i18n.t(cocI18nPath)[0][oldCocType.toLowerCase()] : ''
          newValue = formatConditionsOfContracts(newCocType, isOldTerm, cocI18nPath)
          oldValue = formatConditionsOfContracts(oldCocType, isOldTerm, cocI18nPath)
          if (newCocType && newCocType !== oldCocType) {
            return [{
              action: oldCocType ? 'E' : 'N',
              oldValue,
              value: newValue,
              title: i18n.t('tender.general.conditionsOfContracts.label'),
            }]
          }
          return null
        },
      },
    },
    title: 'General',
  },
  scopeOfWorks: {
    fields: {
      generalDesc: {
        diff: (oldTender, newTender) => {
          if ((newTender.scopeOfWorks || {}).generalDesc !== (oldTender.scopeOfWorks || {}).generalDesc) {
            return [{
              action: 'E',
              diffMatchPatchResult: getDiffMatchPatch(
                strip(oldTender.scopeOfWorks.generalDesc),
                strip(newTender.scopeOfWorks.generalDesc),
              ),
              title: 'General Description of Work',
            }]
          }
          return null
        },
      },
      generalSpec: {
        diff: (oldTender, newTender) => {
          if ((newTender.scopeOfWorks || {}).generalSpec !== (oldTender.scopeOfWorks || {}).generalSpec) {
            return [{
              action: 'E',
              diffMatchPatchResult: getDiffMatchPatch(
                strip(oldTender.scopeOfWorks.generalSpec),
                strip(newTender.scopeOfWorks.generalSpec),
              ),
              title: 'General Specifications',
            }]
          }
          return null
        },
      },
      particularDesc: {
        diff: (oldTender, newTender) => {
          if ((newTender.scopeOfWorks || {}).particularDesc !== (oldTender.scopeOfWorks || {}).particularDesc) {
            return [{
              action: 'E',
              diffMatchPatchResult: getDiffMatchPatch(
                strip(oldTender.scopeOfWorks.particularDesc),
                strip(newTender.scopeOfWorks.particularDesc),
              ),
              title: 'Particular Specifications',
            }]
          }
          return null
        },
      },
      technicalDesc: {
        diff: (oldTender, newTender) => {
          if ((newTender.scopeOfWorks || {}).technicalDesc !== (oldTender.scopeOfWorks || {}).technicalDesc) {
            return [{
              action: 'E',
              diffMatchPatchResult: getDiffMatchPatch(
                strip(oldTender.scopeOfWorks.technicalDesc),
                strip(newTender.scopeOfWorks.technicalDesc),
              ),
              title: 'Technical Specifications/Others',
            }]
          }
          return null
        },
      },
    },
    title: 'Scope of Work',
  },
  service: {
    fields: {
      lineItem: {
        diff: (oldTender, newTender) => {
          const result = []
          const oldRefDict = ((oldTender.service || {}).data || [])
            .reduce((itemsDict, section) => {
              itemsDict[section.id] = section
              section.lineItems.reduce((dict, lineItem) => {
                dict[lineItem.id] = lineItem
                return dict
              }, itemsDict)
              return itemsDict
            }, {})
          newTender.service.data.forEach((section) => {
            if (!oldRefDict[section.id]) {
              result.push({
                action: 'N',
                value: '',
                title: `Section ${section.title}`,
              })
              return result
            }
            if (section.addendumLabel &&
                section.addendumLabel.startsWith('D') &&
                section.addendumLabel !== oldRefDict[section.id].addendumLabel) {
              result.push({
                action: 'D',
                value: '',
                title: `Section ${section.title}`,
              })
              return result
            }
            if (section.addendumLabel &&
                section.addendumLabel.startsWith('E') &&
                (section.title !== oldRefDict[section.id].title)) {
              result.push({
                action: 'E',
                value: `Title: ${section.title}`,
                oldValue: `Title: ${oldRefDict[section.id].title}`,
                title: `Section ${section.title}`,
              })
            }
            if (section.addendumLabel &&
                section.addendumLabel.startsWith('E') &&
                (section.sectionDesc !== oldRefDict[section.id].sectionDesc)) {
              result.push({
                action: 'E',
                value: `Description: ${section.sectionDesc}`,
                oldValue: `Description: ${oldRefDict[section.id].sectionDesc}`,
                title: `Section ${section.title}`,
              })
            }
            section.lineItems.forEach((lineItem) => {
              const parseLineValues = (item = {}) => `Description: ${item.itemDesc}\nQuantity: ${item.itemQty}\nUnit: ${item.itemUnit}`
              if (!oldRefDict[lineItem.id] ||
                  (oldRefDict[lineItem.id].addendumLabel !== lineItem.addendumLabel &&
                    (oldRefDict[lineItem.id].addendumLabel || '').startsWith('D'))) {
                // line item created
                result.push({
                  action: 'N',
                  value: parseLineValues(lineItem),
                  title: `Section ${section.title} : Line ${lineItem.itemNo}`,
                })
              } else if (!lodash.isEqual(lineItem, oldRefDict[lineItem.id])) {
                if (lineItem.addendumLabel && lineItem.addendumLabel.startsWith('D') && lineItem.addendumLabel !== oldRefDict[lineItem.id].addendumLabel) {
                  result.push({
                    action: 'D',
                    value: parseLineValues(lineItem),
                    title: `Section ${section.title} : Line ${lineItem.itemNo}`,
                  })
                  return result
                } else if (lineItem.itemDesc !== oldRefDict[lineItem.id].itemDesc ||
                  lineItem.itemQty !== oldRefDict[lineItem.id].itemQty ||
                  lineItem.itemUnit !== oldRefDict[lineItem.id].itemUnit) {
                  // line item updated
                  result.push({
                    action: 'E',
                    oldValue: parseLineValues(oldRefDict[lineItem.id]),
                    value: parseLineValues(lineItem),
                    title: `Section ${section.title} : Line ${lineItem.itemNo}`,
                  })
                }
              }
            })
          })
          return result
        },
      },
      allowServiceAddOthers: {
        diff: (oldTender, newTender) => {
          if (Boolean(oldTender.allowServiceAddOthers) !== Boolean(newTender.allowServiceAddOthers)) {
            return [{
              action: newTender.allowServiceAddOthers ? 'N' : 'D',
              title: getTenderServiceOthersTitle(),
            }]
          }
          return null
        },
      },
    },
    title: 'Items / Services',
  },
  attachments: {
    fields: {
      description: {
        diff: (oldTender, newTender) => {
          if (newTender.attachmentDescription !== oldTender.attachmentDescription) {
            return [{ action: 'E', oldValue: oldTender.attachmentDescription, value: newTender.attachmentDescription, title: 'Description' }]
          }
          return null
        },
      },
      file: {
        diff: (oldTender, newTender) => {
          if (!lodash.isEqual(newTender.documents, oldTender.documents)) {
            const result = []
            const newDocuments = arrayToDict('id', newTender.documents)
            const oldDocuments = arrayToDict('id', oldTender.documents)
            Object.values(newDocuments).forEach((newDocument) => {
              if (!oldDocuments[newDocument.id]) {
                // new documents
                result.push({ action: 'N', value: newDocument.filename, title: 'File' })
              } else if (newDocument.description !== oldDocuments[newDocument.id].description) {
                // update desc
                result.push({ action: 'E', oldValue: oldDocuments[newDocument.id].description, value: newDocument.description, title: newDocument.filename })
              }
            })
            Object.values(oldDocuments).forEach((oldDocument) => {
              if (!newDocuments[oldDocument.id]) {
                // delete documents
                result.push({ action: 'D', value: oldDocument.filename, title: 'File' })
              }
            })
            return result
          }
          return null
        },
      },
    },
    title: 'Attachments',
  },
}

export function diffTenderFields (oldTender, newTender) {
  return Object.values(tenderFields).reduce((result, tab) => {
    const tabDiff = { title: tab.title }
    tabDiff.fieldDiffs = Object.values(tab.fields).reduce((fieldDiffs, fieldConfig) => {
      try {
        const diffResult = fieldConfig.diff(oldTender, newTender)
        if (diffResult) {
          fieldDiffs.push(...diffResult)
        }
      } catch (err) {}
      return fieldDiffs
    }, [])
    if (tabDiff.fieldDiffs.length > 0) {
      result.push(tabDiff)
    }
    return result
  }, [])
}
